import { Grid, useTheme } from '@mui/material';
import { useTableEditingContext } from 'components/atoms/EditableDataGrid/TableEditingContext';
import Loader, { LoadingStage } from 'components/Loader';
import { getDashboardCanvasStyles } from 'components/theme/theme';
import { Permission } from 'interfaces/permissionOption';
import { Procedure } from 'interfaces/procedure';
import { ProceduresFieldsContext } from 'interfaces/procedure/fields/helpers';
import { ReviewForm } from 'interfaces/reviewForm';
import { flatMap, map } from 'lodash';
import React from 'react';
import { BooleanParam, useQueryParam } from 'use-query-params';
import { useCurrentStateCases } from 'utils/useCurrentStateCases';
import { DataGridInterfaceWrapper } from './DataGrids/DataGridInterfaceWrapper';
import { ErrorMessage } from './ErrorMessage';
import { GalleryViewer } from './GalleryViewer';
import { UnpaginatedGalleryViewer } from './GalleryViewer/UnpaginatedGalleryViewer';
import ProcedureCards from './ProcedureCards/ProcedureCards';
import { useCasesAndSlidesEditingPermissions } from './useCasesAndSlidesEditingPermissions';
import { useCasesViewMode } from './useCasesViewMode';
import { useAllPendingSlides } from './usePendingSlides';

export const CasesDisplay: React.FC<
  React.PropsWithChildren<{
    displayProcedureModeToggle?: boolean;
    pendingSlidesMode?: boolean;
    forms?: ReviewForm[];
  }>
> = ({ displayProcedureModeToggle, pendingSlidesMode, forms }) => {
  const [showGallery, setShowGallery] = useQueryParam('showGallery', BooleanParam);
  const [slidesMode] = useQueryParam('slidesMode', BooleanParam);
  const { hasPermission, hasSomeEditingPermissions } = useCasesAndSlidesEditingPermissions();

  const { casesViewMode } = useCasesViewMode();
  const canUseExperimentalFeatures = hasPermission(Permission.UseNucleaiExperimentalFeatures);
  const procedureViewMode = displayProcedureModeToggle ? casesViewMode : 'card';
  const canUseGalleryMode = displayProcedureModeToggle && canUseExperimentalFeatures;

  const theme = useTheme();

  const { isLoading: isCasesLoading, totalCasesCount, totalSlidesCount, procedures, error } = useCurrentStateCases();
  const { fieldsContext: proceduresFieldsContext } = useTableEditingContext<Procedure, ProceduresFieldsContext>();
  const { allMockCasesOfPendingSlides } = useAllPendingSlides({ enabled: Boolean(pendingSlidesMode && showGallery) });
  const slideCasePairs = flatMap(allMockCasesOfPendingSlides, (procedure: Procedure) =>
    map(procedure.slides, (slide) => ({
      slide,
      case: procedure,
    }))
  );

  const isLoading =
    Boolean(isCasesLoading) ||
    Boolean(proceduresFieldsContext.allCancerTypesLoading) ||
    Boolean(proceduresFieldsContext.stainTypesInStudyLoading);

  return (
    <>
      {error ? (
        <ErrorMessage />
      ) : procedureViewMode === 'table' ? (
        <DataGridInterfaceWrapper
          casesInPage={procedures ?? []}
          totalSlides={totalSlidesCount}
          totalCases={totalCasesCount}
          forms={forms}
          isLoading={isLoading}
          disableEditing={!hasSomeEditingPermissions}
          pendingSlidesMode={pendingSlidesMode}
        />
      ) : isLoading ? (
        <Grid
          container
          className="dashboard-page-body"
          justifyContent="center"
          sx={{
            flex: '1 1 auto',
            ...getDashboardCanvasStyles(theme),
          }}
        >
          <Grid item>
            <Loader
              stage={slidesMode || pendingSlidesMode ? LoadingStage.LoadingSlidesList : LoadingStage.LoadingCaseList}
            />
          </Grid>
        </Grid>
      ) : (
        <>
          {canUseGalleryMode &&
            showGallery &&
            (pendingSlidesMode ? (
              <UnpaginatedGalleryViewer slideCasePairs={slideCasePairs} onClose={() => setShowGallery(false)} />
            ) : (
              <GalleryViewer totalCases={totalCasesCount} totalSlides={totalSlidesCount} />
            ))}
          <ProcedureCards
            procedures={procedures ?? []}
            isLoading={isLoading}
            galleryView={procedureViewMode === 'gallery' && canUseGalleryMode}
            pendingSlidesMode={pendingSlidesMode}
          />
        </>
      )}
    </>
  );
};
